/**
 * Diagnosis section component showing facts and interpretation
 * Used within DiagnosisCard to display each diagnostic dimension
 *
 * Supports enriched fields from spec-007 iteration 2:
 * - emotionalRegister badge on Emotion sections
 * - distinctiveness badge + rationale on Brand Linkage sections
 */

import { cn } from '@/lib/utils';
import type {
  DiagnosisSection as DiagnosisSectionType,
  BrandLinkageSection,
  EmotionalRegister,
  DistinctivenessLevel,
} from '@/types';

export interface DiagnosisSectionProps {
  title: string;
  section: DiagnosisSectionType | BrandLinkageSection;
  icon?: React.ReactNode;
  className?: string;
}

/**
 * Check if section is a BrandLinkageSection (has level property)
 */
function isBrandLinkageSection(
  section: DiagnosisSectionType | BrandLinkageSection
): section is BrandLinkageSection {
  return section != null && 'level' in section;
}

/**
 * Check if section has emotionalRegister (Emotion diagnosis sections)
 */
function hasEmotionalRegister(
  section: DiagnosisSectionType | BrandLinkageSection
): section is DiagnosisSectionType & { emotionalRegister: EmotionalRegister } {
  return section != null && 'emotionalRegister' in section && !!(section as DiagnosisSectionType).emotionalRegister;
}

/**
 * Get badge color based on brand linkage level
 */
function getLevelColor(level: 'Low' | 'Medium' | 'High'): string {
  switch (level) {
    case 'High':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'Medium':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    case 'Low':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
}

/**
 * Get badge color + label for emotionalRegister
 */
function getEmotionalRegisterStyle(register: EmotionalRegister): { color: string; label: string } {
  switch (register) {
    case 'brand_building':
      return { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Brand Building' };
    case 'hybrid':
      return { color: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Hybrid' };
    case 'activation':
      return { color: 'bg-orange-100 text-orange-700 border-orange-200', label: 'Activation' };
    default:
      return { color: 'bg-gray-100 text-gray-700 border-gray-200', label: register };
  }
}

/**
 * Get badge color for distinctiveness level
 */
function getDistinctivenessColor(level: DistinctivenessLevel): string {
  switch (level) {
    case 'generic':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'partial':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'distinctive':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'iconic':
      return 'bg-teal-100 text-teal-700 border-teal-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
}

/**
 * Capitalize first letter of a string
 */
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function DiagnosisSection({
  title,
  section,
  icon,
  className,
}: DiagnosisSectionProps) {
  const hasBrandLevel = isBrandLinkageSection(section);
  const hasRegister = hasEmotionalRegister(section);

  return (
    <div className={cn('', className)}>
      {/* Section Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon && <span className="text-gray-400">{icon}</span>}
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        </div>
        <div className="flex items-center gap-2">
          {/* Emotional Register badge (Emotion sections only) */}
          {hasRegister && (() => {
            const register = (section as DiagnosisSectionType & { emotionalRegister: EmotionalRegister }).emotionalRegister;
            const style = getEmotionalRegisterStyle(register);
            return (
              <span
                className={cn('px-2 py-0.5 text-xs font-medium rounded border', style.color)}
                title="System1 classification — does this creative use narrative, character, and place (brand building) or product, offer, and CTA (activation)?"
              >
                {style.label}
              </span>
            );
          })()}
          {/* Brand Linkage level badge */}
          {hasBrandLevel && (
            <span
              className={cn(
                'px-2 py-0.5 text-xs font-medium rounded border',
                getLevelColor(section.level)
              )}
            >
              {section.level}
            </span>
          )}
        </div>
      </div>

      {/* Distinctiveness badge + rationale (Brand Linkage sections only) */}
      {hasBrandLevel && section.distinctiveness && (
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={cn(
                'px-2 py-0.5 text-xs font-medium rounded border',
                getDistinctivenessColor(section.distinctiveness)
              )}
            >
              {capitalize(section.distinctiveness)}
            </span>
            <span className="text-xs text-gray-400">Distinctiveness</span>
          </div>
          {section.distinctivenessRationale && (
            <p className="text-xs text-gray-500 italic leading-relaxed pl-1">
              {section.distinctivenessRationale}
            </p>
          )}
        </div>
      )}

      {/* Facts List */}
      {section.facts.length > 0 && (
        <div className="mb-3">
          <span className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-2 block">
            Facts
          </span>
          <ul className="space-y-1.5">
            {section.facts.map((fact, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-blue-400 mt-0.5 flex-shrink-0">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 8 8">
                    <circle cx="4" cy="4" r="4" />
                  </svg>
                </span>
                <span className="leading-relaxed">{fact}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Interpretation */}
      <div>
        <span className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-2 block">
          Interpretation
        </span>
        <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-lg">
          {section.interpretation}
        </p>
      </div>
    </div>
  );
}
